import { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Comment } from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import CustomDialog from "../../../components/modals/CustomDialog";
import { useAppDispatch } from "../../../app/hooks";
import { getEmployee } from "../processSlices";
import { Employee } from "../types/processTypes";
import { EmployeeRemarksDialog } from "./EmployeeRemarksDialog";

type Props = {
  employee?: Employee | null;
  /**
   * `bar` — always shown, displays the last remark + a button (entry forms).
   * `indicator` — a small chip shown only when a remark exists (table rows).
   */
  mode?: "bar" | "indicator";
};

export function EmployeeRemarksButton({ employee, mode = "indicator" }: Props) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);
  const [dialogEmployee, setDialogEmployee] = useState<Employee | null>(null);
  const [loading, setLoading] = useState(false);

  const lastRemark = employee?.currentStatusRemark?.trim();
  const remarkExists = Boolean(lastRemark);

  const openDialog = async () => {
    if (!employee?._id) return;
    setDialogEmployee(employee);
    setOpen(true);
    setLoading(true);
    const result = await dispatch(getEmployee(String(employee._id)));
    if (getEmployee.fulfilled.match(result)) {
      setDialogEmployee(result.payload);
    }
    setLoading(false);
  };

  if (!employee?._id) return null;
  if (mode === "indicator" && !remarkExists) return null;

  return (
    <>
      {mode === "bar" ? (
        <Paper
          variant="outlined"
          sx={{
            p: 1,
            borderRadius: 1,
            borderColor: remarkExists ? "warning.main" : "divider",
            bgcolor: (th) =>
              remarkExists ? alpha(th.palette.warning.main, 0.08) : undefined,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Comment
              fontSize="small"
              color={remarkExists ? "warning" : "disabled"}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary">
                {t("currentStatusRemark")}
              </Typography>
              <Typography
                variant="body2"
                noWrap
                color={remarkExists ? "text.primary" : "text.secondary"}
              >
                {lastRemark || t("no_remarks_yet")}
              </Typography>
            </Box>
            <Button size="small" color="secondary" onClick={() => void openDialog()}>
              {t("remarks")}
            </Button>
          </Stack>
        </Paper>
      ) : (
        <Tooltip title={lastRemark ?? ""}>
          <Chip
            size="small"
            color="warning"
            variant="outlined"
            icon={<Comment fontSize="small" />}
            label={t("remark")}
            onClick={() => void openDialog()}
            sx={{ cursor: "pointer", maxWidth: 160 }}
          />
        </Tooltip>
      )}

      {open && dialogEmployee && (
        <CustomDialog
          title={t("remarks")}
          isOpen={open}
          handleDialogClose={() => setOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <EmployeeRemarksDialog
            employee={dialogEmployee}
            loading={loading}
            onSaved={(emp) => setDialogEmployee(emp)}
          />
        </CustomDialog>
      )}
    </>
  );
}
